/**
 * Persists in-progress nurturing test automation state per dealer in localStorage,
 * mirroring {@link generationPersistence} for email template generation.
 */

export interface NurturingAutomationSocketMessagePersisted {
  eventName: string;
  data: unknown;
  timestamp: number;
}

export interface PersistedNurturingAutomationState {
  dealerId: string;
  automationInProgress: boolean;
  jobId: string;
  progress: {
    current: number;
    total: number;
  };
  socketMessages: NurturingAutomationSocketMessagePersisted[];
  timestamp: number;
}

const STORAGE_KEY_PREFIX = 'nurturing_test_automation_state_';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function getStorageKey(dealerId: string): string {
  return `${STORAGE_KEY_PREFIX}${dealerId}`;
}

export function saveNurturingAutomationState(
  dealerId: string,
  state: Omit<PersistedNurturingAutomationState, 'timestamp' | 'dealerId'>,
): void {
  try {
    if (!dealerId) return;
    const persistedState: PersistedNurturingAutomationState = {
      ...state,
      dealerId,
      timestamp: Date.now(),
    };
    localStorage.setItem(getStorageKey(dealerId), JSON.stringify(persistedState));
  } catch (error) {
    console.error('Failed to save nurturing automation state:', error);
  }
}

export function loadNurturingAutomationState(
  dealerId: string,
): PersistedNurturingAutomationState | null {
  try {
    if (!dealerId) return null;
    const stored = localStorage.getItem(getStorageKey(dealerId));
    if (!stored) return null;

    const state: PersistedNurturingAutomationState = JSON.parse(stored);
    if (
      !state ||
      state.dealerId !== dealerId ||
      typeof state.automationInProgress !== 'boolean' ||
      !state.progress ||
      typeof state.progress.current !== 'number' ||
      typeof state.progress.total !== 'number'
    ) {
      clearNurturingAutomationState(dealerId);
      return null;
    }

    if (!Array.isArray(state.socketMessages)) {
      state.socketMessages = [];
    }

    const age = Date.now() - (state.timestamp || 0);
    if (age > MAX_AGE_MS) {
      clearNurturingAutomationState(dealerId);
      return null;
    }

    if (!state.automationInProgress) {
      return null;
    }

    return state;
  } catch (error) {
    console.error('Failed to load nurturing automation state:', error);
    return null;
  }
}

export function clearNurturingAutomationState(dealerId: string): void {
  try {
    if (!dealerId) return;
    localStorage.removeItem(getStorageKey(dealerId));
  } catch (error) {
    console.error('Failed to clear nurturing automation state:', error);
  }
}

export function updateNurturingAutomationProgress(
  dealerId: string,
  progress: { current: number; total: number },
): void {
  try {
    if (!dealerId) return;
    const existing = loadNurturingAutomationState(dealerId);
    if (!existing) return;
    saveNurturingAutomationState(dealerId, {
      automationInProgress: existing.automationInProgress,
      jobId: existing.jobId,
      progress,
      socketMessages: existing.socketMessages,
    });
  } catch (error) {
    console.error('Failed to update nurturing automation progress:', error);
  }
}

export function appendNurturingAutomationSocketMessage(
  dealerId: string,
  message: NurturingAutomationSocketMessagePersisted,
): void {
  try {
    if (!dealerId) return;
    const existing = loadNurturingAutomationState(dealerId);
    if (!existing) return;
    saveNurturingAutomationState(dealerId, {
      automationInProgress: existing.automationInProgress,
      jobId: existing.jobId,
      progress: existing.progress,
      socketMessages: [...existing.socketMessages, message].slice(-100),
    });
  } catch (error) {
    console.error('Failed to append nurturing automation socket message:', error);
  }
}
